import { useEffect, useReducer, useRef, type ReactNode } from 'react';
import { Calculator, AlertCircle, Loader2, ChevronRight } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { formatSignedUsd } from '@/lib/money-format';
import type { PricingState } from './usePricing';
import {
  costParts,
  effectiveQwenRegion,
  engineRates,
  ENGINES,
  partsTotal,
  QWEN_REGIONS,
  roundPartsToTotal,
  roundUsd,
  type Engine,
  type Output,
  type QwenRegion,
  type Side,
} from './cost-model';
import { ENGINE_SCENARIOS, type ScenarioChoice } from './calculator-scenarios';
import { calcReducer, initialCalcState, isSharedStream, type CalcAction } from './calculator-state';
import { asEngine, initialEngine, readStoredEngine, storeEngine, storeRegion } from './calculator-engine';
import { formatHours, hoursFromBalance } from './balance-estimate';
import { engineBrand } from './ledger-labels';
import { PricingCard } from './PricingCard';
import { qwenProviderFor } from './pricing-client';

const OUTPUTS: readonly Output[] = ['off', 'text', 'voice'];

const REGION_LABEL_KEY: Record<QwenRegion, string> = {
  sg: 'dashboard.wallet.pricing.regionSg',
  cn: 'dashboard.wallet.pricing.regionCn',
};

/**
 * One radio rendered as a button, in the dashboard's preset-button dialect. `type="checkbox"` is
 * the same button for a switch that is on or off by itself (screen context), with no group name.
 */
function Choice({ name, type = 'radio', checked, onSelect, children }: {
  name?: string;
  type?: 'radio' | 'checkbox';
  checked: boolean;
  onSelect: () => void;
  children: ReactNode;
}) {
  return (
    <label className={`wallet-calc__choice${checked ? ' is-selected' : ''}`}>
      <input type={type} name={name} checked={checked} onChange={onSelect} />
      <span>{children}</span>
    </label>
  );
}

/**
 * What a session costs on the engine you pick, for the thing you do with it.
 *
 * ONE ENGINE AT A TIME. The switch in the header shows Soniox or Qwen, never
 * both: the two are priced by one model (`cost-model.ts`) so the figures are
 * comparable, but a price alone hides what each engine does that the other
 * cannot, and the owner chose not to put them side by side. The line under
 * the price says what the selected engine itself offers.
 *
 * PRESETS, THEN THE SETTINGS THEY STAND FOR. A preset (the app's first-run
 * scenarios, per engine) fills in the controls below it; touching a control
 * selects Custom. Every rule about that, and about two speaking shares that
 * must fit in one hour, is `calculator-state.ts`'s, where the tests reach it.
 *
 * THE PRICE LEADS AND THE PARTS FOLLOW. The estimate is the largest thing on
 * the card; the fold under it says where it came from, and its rows add up to
 * the headline exactly (`roundPartsToTotal`).
 */
export function CostCalculatorCard({ pricing, loading, reload, balanceMicroUsd, latestEngine, openingRegion }: PricingState & {
  /** Null while the wallet status is loading or has failed. */
  balanceMicroUsd: number | null;
  /** The engine of the visitor's latest session; `undefined` while it loads. */
  latestEngine: string | null | undefined;
  /** The Qwen region to open on (`initialRegion`): the page works it out once, for the card and the balance estimate alike. */
  openingRegion: QwenRegion;
}) {
  const { t, locale } = useI18n();
  const [state, dispatch] = useReducer(calcReducer, undefined, () =>
    initialCalcState(initialEngine(readStoredEngine(), latestEngine), openingRegion)
  );
  // A visitor who has picked an engine on an earlier visit keeps it. Otherwise
  // the latest session's engine is adopted when it arrives — unless the
  // visitor has already touched the card, which must not change under them.
  // Read once: a ref's initial value is evaluated on every render.
  const touched = useRef<boolean | null>(null);
  if (touched.current === null) touched.current = readStoredEngine() !== null;
  useEffect(() => {
    const hinted = asEngine(latestEngine);
    if (touched.current || !hinted) return;
    touched.current = true;
    dispatch({ type: 'pickEngine', engine: hinted });
  }, [latestEngine]);

  // The region follows `openingRegion` until the visitor touches the card, so
  // the card and the balance estimate keep one region when the latest session
  // arrives late. It is not the engine's `touched`: a remembered engine does not
  // say which region the latest session ran in.
  const regionFollows = useRef(true);
  useEffect(() => {
    if (regionFollows.current) dispatch({ type: 'pickRegion', region: openingRegion });
  }, [openingRegion]);

  const act = (action: CalcAction) => {
    touched.current = true;
    regionFollows.current = false;
    dispatch(action);
  };
  const pickEngine = (engine: Engine) => {
    storeEngine(engine);
    act({ type: 'pickEngine', engine });
  };
  const pickRegion = (region: QwenRegion) => {
    storeRegion(region);
    act({ type: 'pickRegion', region });
  };

  const { engine, region: pickedRegion, settings } = state;
  // What the payload can price: Singapore while Beijing has no entry. The pick itself is kept.
  const region = effectiveQwenRegion(pricing, pickedRegion);
  const rates = engineRates(pricing, engine, region);
  const shared = isSharedStream(state);
  const bothOff = settings.them === 'off' && settings.me === 'off';
  const parts = rates ? costParts(rates, settings, shared) : null;
  const total = parts ? partsTotal(parts) : 0;
  const [connectionShown, textShown, voiceShown, screenShown] = parts
    ? roundPartsToTotal([parts.connection, parts.text, parts.voice, parts.screen], 2)
    : [0, 0, 0, 0];
  // Screen context is Qwen's, and only from a payload that publishes its rate.
  const hasScreenContext = engine === 'qwen' && rates?.screen !== undefined;

  // `roundUsd` decides the value and `toFixed` only renders it.
  const money = (usd: number) => `$${roundUsd(usd, 2).toFixed(2)}`;
  const perHour = (usd: number) => t('dashboard.wallet.pricing.perHourApprox').replace('{amount}', money(usd));
  const count = (n: number) => Math.round(n).toLocaleString(locale);
  const hours = balanceMicroUsd !== null && balanceMicroUsd > 0 ? hoursFromBalance(balanceMicroUsd, total) : null;

  const shareText = (percent: number) =>
    t('dashboard.wallet.pricing.shareValue')
      .replace('{count}', count(percent))
      .replace('{minutes}', count(percent * 0.6));

  const shareLabel = (side: Side) =>
    t(side === 'them' ? 'dashboard.wallet.pricing.shareThem' : 'dashboard.wallet.pricing.shareMe');

  const sideControls = (side: Side) => {
    const output = settings[side];
    const share = side === 'them' ? settings.themShare : settings.meShare;
    const voiceKey = side === 'them' ? 'output.voiceThem' : 'output.voiceMe';
    return (
      <fieldset className="wallet-calc__group">
        <legend>{t(side === 'them' ? 'dashboard.wallet.pricing.sideThem' : 'dashboard.wallet.pricing.sideMe')}</legend>
        <div className="wallet-calc__choices">
          {OUTPUTS.map((o) => (
            <Choice
              key={o}
              name={`calc-${side}`}
              checked={output === o}
              onSelect={() => act({ type: 'setOutput', side, output: o })}
            >
              {t(`dashboard.wallet.pricing.${o === 'voice' ? voiceKey : `output.${o}`}`)}
            </Choice>
          ))}
        </div>
        {/* Only for a side that is translated: an untranslated side's
            speaking time changes nothing on the bill. Both rows reserve the
            other row's label and the widest value (100%), so the two sliders
            start and end at the same place, in every language, and neither
            changes length while it is dragged. */}
        {output !== 'off' && (
          <div className="wallet-calc__share">
            <label className="wallet-calc__share-label" htmlFor={`calc-share-${side}`}>
              <span>{shareLabel(side)}</span>
              <span className="wallet-calc__share-reserve" aria-hidden="true">
                {shareLabel(side === 'them' ? 'me' : 'them')}
              </span>
            </label>
            <input
              id={`calc-share-${side}`}
              className="wallet-calc__slider"
              type="range"
              min={0}
              max={100}
              step={5}
              value={share}
              aria-valuetext={shareText(share)}
              // Read by the track's gradient: a range input has no
              // cross-browser way to paint the travelled part of its track.
              style={{ ['--wc-fill' as string]: `${share}%` }}
              onChange={(e) => act({ type: 'setShare', side, percent: Number(e.target.value) })}
            />
            <span className="wallet-calc__share-value">
              <span>{shareText(share)}</span>
              <span className="wallet-calc__share-reserve" aria-hidden="true">{shareText(100)}</span>
            </span>
          </div>
        )}
      </fieldset>
    );
  };

  const scenarios: ScenarioChoice[] = [...ENGINE_SCENARIOS[engine], 'custom'];

  return (
    <div className="wallet-card wallet-card--full">
      <div className="wallet-card__header wallet-calc__header">
        <Calculator size={20} />
        <h2>{t('dashboard.wallet.pricing.calcTitle')}</h2>
        <div className="wallet-calc__choices wallet-calc__engines" role="radiogroup" aria-label={t('dashboard.wallet.pricing.engine')}>
          {ENGINES.map((e) => (
            <Choice key={e} name="calc-engine" checked={engine === e} onSelect={() => pickEngine(e)}>
              {t('dashboard.wallet.ledger.poweredBy').replace('{name}', engineBrand(e))}
            </Choice>
          ))}
        </div>
        {/* Qwen's region, under the engine switch: Alibaba bills Singapore and
            Beijing at different prices. Soniox has none; and while Beijing has
            no rate there is nothing to choose between. */}
        {engine === 'qwen' && qwenProviderFor(pricing, 'cn') !== null && (
          <div className="wallet-calc__choices wallet-calc__regions" role="radiogroup" aria-label={t('dashboard.wallet.pricing.region')}>
            {QWEN_REGIONS.map((r) => (
              <Choice key={r} name="calc-region" checked={region === r} onSelect={() => pickRegion(r)}>
                {t(REGION_LABEL_KEY[r])}
              </Choice>
            ))}
          </div>
        )}
      </div>
      <div className="wallet-card__content">
        {loading ? (
          <div className="wallet-card__loading"><Loader2 className="animate-spin" size={20} /></div>
        ) : !rates || !parts ? (
          <div className="wallet-card__unavailable">
            <AlertCircle size={24} />
            <p>{t('dashboard.wallet.pricing.error')}</p>
            <button type="button" className="wallet-card__submit-btn" onClick={reload}>
              {t('dashboard.wallet.pricing.retry')}
            </button>
          </div>
        ) : (
          <>
            <fieldset className="wallet-calc__group">
              <legend>{t('dashboard.wallet.pricing.calcScenario')}</legend>
              <div className="wallet-calc__choices">
                {scenarios.map((id) => (
                  <Choice
                    key={id}
                    name="calc-scenario"
                    checked={state.scenario === id}
                    onSelect={() => act({ type: 'pickScenario', scenario: id })}
                  >
                    {t(`dashboard.wallet.pricing.scenario.${id}.title`)}
                  </Choice>
                ))}
              </div>
              <p className="wallet-calc__hint">{t(`dashboard.wallet.pricing.scenario.${state.scenario}.desc`)}</p>
            </fieldset>

            {sideControls('them')}
            {sideControls('me')}

            {hasScreenContext && (
              <div className="wallet-calc__group">
                <div className="wallet-calc__choices">
                  <Choice
                    type="checkbox"
                    checked={settings.screenContext === true}
                    onSelect={() => act({ type: 'setScreenContext', on: settings.screenContext !== true })}
                  >
                    {t('dashboard.wallet.pricing.screenContext')}
                  </Choice>
                </div>
                <p className="wallet-calc__hint">{t('dashboard.wallet.pricing.screenContextHint')}</p>
              </div>
            )}

            {bothOff ? (
              <p className="wallet-calc__none">{t('dashboard.wallet.pricing.needOneSide')}</p>
            ) : (
              <div className="wallet-calc__result">
                <span className="wallet-calc__result-label">{t('dashboard.wallet.pricing.calcEstimate')}</span>
                {/* An <output>: it is the computed result of the controls
                    above it, and screen readers announce it when it changes. */}
                <output className="wallet-calc__result-amount">{perHour(total)}</output>
                {hours !== null && balanceMicroUsd !== null && (
                  <span className="wallet-calc__balance">
                    {/* Under an hour gets its own sentence: "covers about
                        < 1 hours" reads as a typo in every language. */}
                    {(hours < 1
                      ? t('dashboard.wallet.pricing.balanceLastsUnderOne')
                      : t('dashboard.wallet.pricing.balanceLasts').replace('{hours}', formatHours(hours, locale))
                    ).replace('{balance}', formatSignedUsd(balanceMicroUsd))}
                  </span>
                )}
              </div>
            )}
            <p className="wallet-calc__trait">{t(`dashboard.wallet.pricing.trait.${engine}`)}</p>
            {/* Face to face is Soniox's: one shared stream, one connection. */}
            {engine === 'soniox' && settings.them !== 'off' && settings.me !== 'off' && (
              <p className="wallet-card__pricing-note">
                {shared
                  ? t('dashboard.wallet.pricing.sharedActive')
                  : t('dashboard.wallet.pricing.sharedHint').replace('{amount}', money(rates.connection))}
              </p>
            )}
            <p className="wallet-card__pricing-note">{t('dashboard.wallet.pricing.settlement')}</p>

            {!bothOff && (
              <details className="wallet-calc__fold">
                <summary>
                  <ChevronRight size={16} aria-hidden="true" />
                  {t('dashboard.wallet.pricing.calcFactors')}
                </summary>
                <table className="wallet-calc__factors">
                  <tbody>
                    <tr>
                      <th scope="row">
                        {t('dashboard.wallet.pricing.partConnection').replace('{count}', count(parts.connections))}
                      </th>
                      <td className="wallet-calc__factor-cost">{perHour(connectionShown)}</td>
                    </tr>
                    <tr>
                      <th scope="row">{t('dashboard.wallet.pricing.partText')}</th>
                      <td className="wallet-calc__factor-cost">{perHour(textShown)}</td>
                    </tr>
                    <tr>
                      <th scope="row">{t('dashboard.wallet.pricing.partVoice')}</th>
                      <td className="wallet-calc__factor-cost">{perHour(voiceShown)}</td>
                    </tr>
                    {hasScreenContext && (
                      <tr>
                        <th scope="row">{t('dashboard.wallet.pricing.partScreen')}</th>
                        <td className="wallet-calc__factor-cost">{perHour(screenShown)}</td>
                      </tr>
                    )}
                    <tr className="wallet-calc__factor-total">
                      <th scope="row">{t('dashboard.wallet.pricing.partTotal')}</th>
                      <td className="wallet-calc__factor-cost">{perHour(total)}</td>
                    </tr>
                  </tbody>
                </table>
              </details>
            )}
          </>
        )}
      </div>
      {rates && (
        <details className="wallet-calc__pricing-fold">
          <summary>
            <ChevronRight size={16} aria-hidden="true" />
            {t('dashboard.wallet.fullPricing')}
          </summary>
          <PricingCard pricing={pricing} engine={engine} region={region} />
        </details>
      )}
    </div>
  );
}
